import { useState } from "react";
import { useTranslation } from "react-i18next";

import { TextFieldGrey } from "../../Default/LekUI/TextField";
import List from "./CategoriesFilters/List";
import Filter from "./DefaultFilters";

export default function DefaultFilterList({ selectedData, ...props }) {
	const { t } = useTranslation();
	const [searchText, setSearchText] = useState("");

	const isCorrectElement = (element) => {
		if (!element?.title) {
			return false;
		}

		return element.title.toLowerCase().startsWith(searchText.toLowerCase());
	};

	const setSelectedData = ({ tags, subtags, departments }) => {
		return props.setSelectedData({
			departments: departments.map(
				({
					dzial_related,
					relatedItems,
					tagdzial_related,
					dzial_liczba_pytan,
					subtag_liczba_pytan,
					tag_liczba_pytan,
					...data
				}) => data,
			),
			tags: tags.map(
				({ dzial_related, relatedItems, tagdzial_related, ...data }) => data,
			),
			subtags: subtags.map(
				({ dzial_related, relatedItems, tagdzial_related, ...data }) => data,
			),
		});
	};

	const getRelatedItems = (element) => {
		if (element.dzial) {
			return props.list.find((e) => e.index === element.index).relatedItems;
		}

		if (element.tag || element.tag === "") {
			const department = props.list.find((e) => e.index === element.dzialId);
			return department.relatedItems.find((t) => t.index === element.index)
				.relatedItems;
		}

		const department = props.list.find((e) => e.index === element.dzialId);
		const tag = department.relatedItems.find((t) => t.index === element.tagId);

		return tag.relatedItems((t) => t.index === element.index).relatedItems;
	};

	const getList = (list = props.list) => {
		return list
			.map((element) => {
				if (isCorrectElement(element)) {
					return element;
				}
				const relatedItems = getList(element.relatedItems);
				if (relatedItems.length !== 0) {
					return {
						...element,
						relatedItems,
					};
				}
				return null;
			})
			.filter(Boolean);
	};

	const renderSearch = () => {
		return (
			<TextFieldGrey
				variant="outlined"
				margin="none"
				fullWidth
				placeholder={t("filters.exams.departments.search")}
				value={searchText}
				onChange={(e) => setSearchText(e.target.value.toLowerCase())}
				size="small"
			/>
		);
	};

	const isElementSelected = (element) => {
		return selectedData[getKey(element)].some(
			(item) => item.index === element.index,
		);
	};

	const getKey = (element) => {
		return element.dzial ? "departments" : element.tag ? "tags" : "subtags";
	};

	const onSelect = (element) => {
		if (element.dzial) {
			if (isElementSelected(element)) {
				return setSelectedData({
					departments: selectedData.departments.filter(
						(d) => d.index !== element.index,
					),
					tags: selectedData.tags.filter((t) => t.dzialId !== element.index),
					subtags: selectedData.subtags.filter(
						(st) => st.dzialId !== element.index,
					),
				});
			}

			return setSelectedData({
				departments: [...selectedData.departments, element],
				tags: [...selectedData.tags, ...getRelatedItems(element)],
				subtags: [
					...selectedData.subtags,
					...getRelatedItems(element).flatMap((tag) =>
						getRelatedItems(tag).map((subTags) => subTags),
					),
				],
			});
		}

		if (element.tag) {
			if (isElementSelected(element)) {
				const tags = selectedData.tags.filter((t) => t.index !== element.index);
				const subtags = selectedData.subtags.filter(
					(st) => st.tagId !== element.index,
				);
				return setSelectedData({
					departments: selectedData.departments.filter(
						(d) => d.index !== element.dzialId,
					),
					tags,
					subtags,
				});
			}

			const tags = [...selectedData.tags, element];
			const department = props.list.find(
				(dep) => dep.index === element.dzialId,
			);
			const isFull =
				department.relatedItems.length ===
				tags.filter((st) => st.dzialId === element.dzialId).length;

			return setSelectedData({
				departments: isFull
					? [...selectedData.departments, department]
					: selectedData.departments,
				tags,
				subtags: [...selectedData.subtags, ...getRelatedItems(element)],
			});
		}

		if (isElementSelected(element)) {
			return setSelectedData({
				departments: selectedData.departments.filter(
					(d) => d.index !== element.dzialId,
				),
				tags: selectedData.tags.filter((d) => d.index !== element.tagId),
				subtags: selectedData.subtags.filter(
					(st) => st.index !== element.index,
				),
			});
		}

		const department = props.list.find((dep) => dep.index === element.dzialId);
		const tag = department.relatedItems.find((t) => t.index === element.tagId);
		const subtags = [...selectedData.subtags, element];
		const isTagFull =
			tag.relatedItems.length ===
			subtags.filter((st) => st.tagId === element.tagId).length;
		const tags = isTagFull ? [...selectedData.tags, tag] : selectedData.tags;
		const isFull =
			department.relatedItems.length ===
			tags.filter((t) => t.dzialId === element.dzialId).length;

		return setSelectedData({
			departments: isFull
				? [...selectedData.departments, department]
				: selectedData.departments,
			tags,
			subtags,
		});
	};

	const isChecked = (element) => {
		return selectedData[getKey(element)].some(
			(data) => data.index === element.index,
		);
	};

	const isIndeterminate = (element) => {
		if (element.dzial) {
			return (
				!selectedData.departments.some((d) => d.index === element.index) &&
				(selectedData.tags.some((t) => t.dzialId === element.index) ||
					selectedData.subtags.some((st) => st.dzialId === element.index))
			);
		}

		if (element.tag) {
			return (
				!selectedData.tags.some((t) => t.index === element.index) &&
				selectedData.subtags.some((st) => st.tagId === element.index)
			);
		}

		return false;
	};

	const renderList = () => {
		return (
			<div className="flex gap-8 flex-col">
				{renderSearch()}
				<List
					onSelect={onSelect}
					isChecked={isChecked}
					isIndeterminate={isIndeterminate}
					list={getList()}
					searchText={searchText}
				/>
			</div>
		);
	};

	return <Filter {...props} renderFilterDetails={renderList} />;
}
